import React, { useEffect, useMemo, useState, useRef } from 'react';
import api from '../services/api';
import { 
  Clock, 
  Edit3, 
  Save, 
  X, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  GripVertical,
  Trash2,
  MoreVertical,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Eye,
  ExternalLink,
  Copy,
  Share2
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Schedule {
  id: string;
  title: string;
  description?: string;
  task_id?: string;
  project_id?: string;
  start_time: string;
  end_time: string;
  is_all_day?: number;
  break_minutes?: number;
}

interface EditingBlock {
  id: string;
  title: string;
  description: string;
  start_time: string;
  end_time: string;
}

interface GroupedBlocks {
  startHour: number;
  endHour: number;
  blocks: Schedule[];
  totalDuration: number;
}

function iso(date: Date) {
  return date.toISOString();
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getTimeSlot(time: Date): string {
  const hour = time.getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function getTimeSlots(date: Date) {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    const time = new Date(date);
    time.setHours(hour, 0, 0, 0);
    slots.push({
      hour,
      time,
      label: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      period: hour < 12 ? 'AM' : 'PM'
    });
  }
  return slots;
}

function getBlocksForHour(items: Schedule[], hour: number) {
  return items.filter(block => {
    const start = new Date(block.start_time);
    const end = new Date(block.end_time);
    const blockStartHour = start.getHours();
    const blockEndHour = end.getHours();
    return blockStartHour <= hour && blockEndHour >= hour;
  });
}

function groupBlocksByTime(items: Schedule[]): GroupedBlocks[] {
  if (items.length === 0) return [];
  
  const sortedItems = [...items].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  const groups: GroupedBlocks[] = [];
  
  let currentGroup: GroupedBlocks | null = null;
  
  for (const block of sortedItems) {
    const start = new Date(block.start_time);
    const end = new Date(block.end_time);
    const startHour = start.getHours();
    const endHour = end.getHours();
    
    if (!currentGroup) {
      currentGroup = {
        startHour,
        endHour,
        blocks: [block],
        totalDuration: (end.getTime() - start.getTime()) / (1000 * 60)
      };
    } else {
      // Check if this block overlaps or is adjacent to current group
      const timeGap = (start.getTime() - new Date(currentGroup.blocks[currentGroup.blocks.length - 1].end_time).getTime()) / (1000 * 60);
      
      if (timeGap <= 30) { // 30 minutes gap or less
        currentGroup.blocks.push(block);
        currentGroup.endHour = Math.max(currentGroup.endHour, endHour);
        currentGroup.totalDuration += (end.getTime() - start.getTime()) / (1000 * 60);
      } else {
        groups.push(currentGroup);
        currentGroup = {
          startHour,
          endHour,
          blocks: [block],
          totalDuration: (end.getTime() - start.getTime()) / (1000 * 60)
        };
      }
    }
  }
  
  if (currentGroup) {
    groups.push(currentGroup);
  }
  
  return groups;
}

const TimeMap: React.FC = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [items, setItems] = useState<Schedule[]>([]);
  const [date, setDate] = useState(() => new Date());
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingBlock, setEditingBlock] = useState<EditingBlock | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'list'>('timeline');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<Schedule | null>(null);
  const [newBlockData, setNewBlockData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    duration: 60
  });

  const dayRange = useMemo(() => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [date]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/schedules', { params: { start: dayRange.start, end: dayRange.end } });
        setItems(res.data);
      } catch (err) {
        console.error('Failed to load schedules:', err);
        setError('Failed to load schedules');
      }
    })();
  }, [dayRange.start, dayRange.end]);

  const createBlock = async () => {
    if (!newBlockData.title || !newBlockData.start_time) {
      setError('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    setError(null);
    
    try {
      const startTime = new Date(newBlockData.start_time);
      const endTime = new Date(startTime.getTime() + newBlockData.duration * 60 * 1000);
      
      const res = await api.post('/api/schedules', {
        title: newBlockData.title,
        description: newBlockData.description,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        break_minutes: 10
      });
      
      setItems(prev => [...prev, res.data]);
      setShowCreateModal(false);
      setNewBlockData({ title: '', description: '', start_time: '', end_time: '', duration: 60 });
      setSuccessMessage('Block created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to create block:', err);
      setError(err.response?.data?.error || 'Failed to create block');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteBlock = async (id: string) => {
    try {
      await api.delete(`/api/schedules/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      setEditingBlock(null);
      setSelectedBlock(null);
      setSuccessMessage('Block deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to delete block:', err);
      setError('Failed to delete block');
    }
  };

  const startEditing = (block: Schedule) => {
    setEditingBlock({
      id: block.id,
      title: block.title,
      description: block.description || '',
      start_time: block.start_time,
      end_time: block.end_time
    });
    setSelectedBlock(null); // Close detail view when editing
  };

  const saveBlock = async () => {
    if (!editingBlock) return;
    
    try {
      const res = await api.put(`/api/schedules/${editingBlock.id}`, {
        title: editingBlock.title,
        description: editingBlock.description,
        start_time: editingBlock.start_time,
        end_time: editingBlock.end_time
      });
      
      setItems(prev => prev.map(item => 
        item.id === editingBlock.id ? { ...item, ...res.data } : item
      ));
      setEditingBlock(null);
      setSuccessMessage('Block updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update block:', err);
      setError('Failed to update block');
    }
  };

  const getCurrentTimePosition = () => {
    const now = new Date();
    const isToday = now.toDateString() === date.toDateString();
    if (!isToday) return null;
    
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    return (totalMinutes / (24 * 60)) * 100;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Time Map
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="hidden sm:flex bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'timeline'
                      ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Timeline
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  List
                </button>
              </div>

              {/* Navigation */}
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setDate(d => new Date(d.getTime() - 86400000))} 
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </button>
                
                <button 
                  onClick={() => setDate(new Date())} 
                  className="px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors text-sm font-medium"
                >
                  Today
                </button>
                
                <button 
                  onClick={() => setDate(d => new Date(d.getTime() + 86400000))} 
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </button>
              </div>
              
              <button 
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Block</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-700 dark:text-green-300">{successMessage}</span>
            </div>
            <button onClick={() => setSuccessMessage(null)} className="text-green-500 hover:text-green-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {viewMode === 'timeline' ? (
          <TimelineView 
            items={items}
            date={date}
            editingBlock={editingBlock}
            onEdit={startEditing}
            onSave={saveBlock}
            onCancel={() => setEditingBlock(null)}
            onDelete={deleteBlock}
            onSelect={setSelectedBlock}
            currentTimePosition={getCurrentTimePosition()}
          />
        ) : (
          <ListView 
            items={items}
            editingBlock={editingBlock}
            onEdit={startEditing}
            onSave={saveBlock}
            onCancel={() => setEditingBlock(null)}
            onDelete={deleteBlock}
            onSelect={setSelectedBlock}
          />
        )}
      </div>

      {/* Block Detail Modal */}
      {selectedBlock && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                      {selectedBlock.title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {formatTime(new Date(selectedBlock.start_time))} - {formatTime(new Date(selectedBlock.end_time))}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => copyToClipboard(`${selectedBlock.title}\n${selectedBlock.description || ''}`)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => startEditing(selectedBlock)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => setSelectedBlock(null)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
              
              {selectedBlock.description && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</h4>
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {selectedBlock.description}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Duration:</span>
                  <span className="ml-2 text-slate-700 dark:text-slate-300">
                    {Math.round((new Date(selectedBlock.end_time).getTime() - new Date(selectedBlock.start_time).getTime()) / (1000 * 60))} minutes
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Time of day:</span>
                  <span className="ml-2 text-slate-700 dark:text-slate-300">
                    {getTimeSlot(new Date(selectedBlock.start_time))}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => setSelectedBlock(null)}
                  className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => startEditing(selectedBlock)}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg transition-all duration-200"
                >
                  Edit Block
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Block Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Create New Block</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={newBlockData.title}
                  onChange={(e) => setNewBlockData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter block title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newBlockData.description}
                  onChange={(e) => setNewBlockData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter description"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  value={newBlockData.start_time}
                  onChange={(e) => setNewBlockData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={newBlockData.duration}
                  onChange={(e) => setNewBlockData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="15"
                  max="480"
                  step="15"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createBlock}
                disabled={isCreating}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 text-white rounded-lg transition-all duration-200"
              >
                {isCreating ? 'Creating...' : 'Create Block'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Timeline View Component
const TimelineView: React.FC<{
  items: Schedule[];
  date: Date;
  editingBlock: EditingBlock | null;
  onEdit: (block: Schedule) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onSelect: (block: Schedule) => void;
  currentTimePosition: number | null;
}> = ({ items, date, editingBlock, onEdit, onSave, onCancel, onDelete, onSelect, currentTimePosition }) => {
  const groupedBlocks = groupBlocksByTime(items);

  if (items.length === 0) {
    return (
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
        <div className="p-12 text-center">
          <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-12 h-12 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No blocks scheduled</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Your timeline is empty for this day. Create your first block to start planning your day.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>Morning</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span>Afternoon</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
              <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
              <span>Evening</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      {/* Time Header */}
      <div className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-slate-700 dark:text-slate-300">Timeline Overview</h3>
          <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>{groupedBlocks.length} time blocks</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>{Math.round(groupedBlocks.reduce((acc, group) => acc + group.totalDuration, 0))} min total</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-6">
        <div className="space-y-4">
          {groupedBlocks.map((group, groupIndex) => (
            <div key={groupIndex} className="relative">
              {/* Time Range Label */}
              <div className="flex items-center space-x-3 mb-3">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {formatTime(new Date(group.blocks[0].start_time))} - {formatTime(new Date(group.blocks[group.blocks.length - 1].end_time))}
                  </span>
                </div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-600"></div>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {Math.round(group.totalDuration)} min
                </span>
              </div>

              {/* Blocks in Group */}
              <div className="space-y-3">
                {group.blocks.map((block) => {
                  const start = new Date(block.start_time);
                  const end = new Date(block.end_time);
                  const isEditing = editingBlock?.id === block.id;
                  
                  return (
                    <div
                      key={block.id}
                      className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-md ${
                        isEditing
                          ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20'
                          : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                      onClick={() => !isEditing && onSelect(block)}
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <input
                              type="text"
                              value={editingBlock?.title || ''}
                              onChange={(e) => onEdit({ ...block, title: e.target.value })}
                              className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            />
                            <div className="flex items-center space-x-2 ml-3">
                              <button
                                onClick={onSave}
                                className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={onCancel}
                                className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                          <textarea
                            value={editingBlock?.description || ''}
                            onChange={(e) => onEdit({ ...block, description: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none"
                            placeholder="Add description..."
                            rows={3}
                          />
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {formatTime(start)} - {formatTime(end)}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                                <Clock className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <h4 className="font-medium text-slate-900 dark:text-white">
                                  {block.title}
                                </h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                  {formatTime(start)} - {formatTime(end)}
                                </p>
                              </div>
                            </div>
                            {block.description && (
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 ml-11 line-clamp-2">
                                {block.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-1 ml-4">
                            <button
                              onClick={(e) => { e.stopPropagation(); onEdit(block); }}
                              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
                              className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// List View Component
const ListView: React.FC<{
  items: Schedule[];
  editingBlock: EditingBlock | null;
  onEdit: (block: Schedule) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onSelect: (block: Schedule) => void;
}> = ({ items, editingBlock, onEdit, onSave, onCancel, onDelete, onSelect }) => {
  const sortedItems = [...items].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

  return (
    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">All Blocks</h3>
          <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
            <span>{sortedItems.length} blocks</span>
            <span>•</span>
            <span>{Math.round(sortedItems.reduce((acc, item) => acc + (new Date(item.end_time).getTime() - new Date(item.start_time).getTime()) / (1000 * 60), 0))} min total</span>
          </div>
        </div>
        
        {sortedItems.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">No blocks scheduled for this day</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedItems.map((block) => {
              const start = new Date(block.start_time);
              const end = new Date(block.end_time);
              const isEditing = editingBlock?.id === block.id;
              
              return (
                <div
                  key={block.id}
                  className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-md ${
                    isEditing
                      ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                  onClick={() => !isEditing && onSelect(block)}
                >
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <input
                          type="text"
                          value={editingBlock?.title || ''}
                          onChange={(e) => onEdit({ ...block, title: e.target.value })}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                        <div className="flex items-center space-x-2 ml-3">
                          <button
                            onClick={onSave}
                            className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={onCancel}
                            className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={editingBlock?.description || ''}
                        onChange={(e) => onEdit({ ...block, description: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none"
                        placeholder="Add description..."
                        rows={3}
                      />
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {formatTime(start)} - {formatTime(end)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                            <Clock className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900 dark:text-white">
                              {block.title}
                            </h4>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {formatTime(start)} - {formatTime(end)}
                            </p>
                          </div>
                        </div>
                        {block.description && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 ml-11 line-clamp-2">
                            {block.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 ml-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(block); }}
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4 text-slate-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeMap;

